/**
 * HistoryReportsTab — the "History Reports" module under Reporting &
 * Analytics. An Admin/CEO-only archive of every Daily Report and Site
 * Visit Report ever submitted by agents and HODs.
 *
 * Layout:
 *   - Sales / Marketing sub-tabs (department scope)
 *   - Daily / Monthly / Yearly grouping toggle
 *   - Filters: custom date range, agent, HOD, report type, free-text search
 *   - Four type tiles (Agent Daily / Agent Visit / HOD Daily / HOD Visit)
 *     that double as quick filters
 *   - Reports grouped under period headers, each row expandable
 *   - Export to CSV (opens in Excel) and Print-to-PDF
 *
 * Data: GET /api/ceo/history-reports (gated to Admin/CEO server-side).
 * The server scopes by department + date range; agent / HOD / type /
 * search are applied client-side so typing stays snappy.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import HelpInfo from './HelpInfo';
import {
  History, FileText, MapPin, Calendar, Download, Printer, Search,
  RefreshCw, ChevronDown, ChevronRight, Users, Crown, CheckCircle2, XCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};
const fmtDateTime = (raw) => {
  if (!raw) return '—';
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};
const csvEscape = (v) => {
  const s = (v == null ? '' : String(v)).replace(/\r?\n/g, ' ');
  return /[",]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

// Human-readable label for a period group key.
const periodLabel = (key, period) => {
  if (period === 'yearly') return key;                       // "2026"
  if (period === 'monthly') {                                 // "2026-05"
    const [y, m] = key.split('-');
    return `${MONTHS[Number(m) - 1] || m} ${y}`;
  }
  const d = new Date(`${key}T12:00:00`);                      // "2026-05-13"
  return Number.isNaN(d.getTime())
    ? key
    : d.toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
};

const TYPE_META = {
  agent_daily: { label: 'Agent Daily', icon: FileText, tone: 'violet' },
  agent_visit: { label: 'Agent Site Visit', icon: MapPin, tone: 'emerald' },
  hod_daily: { label: 'HOD Daily', icon: Crown, tone: 'blue' },
  hod_visit: { label: 'HOD Site Visit', icon: MapPin, tone: 'amber' },
};

const HistoryReportsTab = ({ initialDepartment = 'sales' }) => {
  const { token } = useAuth();

  // Department sub-tab + period grouping. `initialDepartment` lets the
  // parent open this straight onto Sales or Marketing based on which
  // Reporting & Analytics menu item the user clicked.
  const [department, setDepartment] = useState(
    initialDepartment === 'marketing' ? 'marketing' : 'sales',
  );
  const [period, setPeriod] = useState('daily');

  // Filters.
  const [from, setFrom] = useState(daysAgoISO(30));
  const [to, setTo] = useState(todayISO());
  const [agentId, setAgentId] = useState('all');
  const [hodId, setHodId] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');

  // Data.
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [hods, setHods] = useState([]);
  const [expandedKey, setExpandedKey] = useState(null);

  // ---- roster for the Agent / HOD dropdowns (fetched once) ----
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const [aRes, hRes] = await Promise.all([
          fetch(`${API}/api/hod/messageable-agents`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API}/api/ceo/messageable-hods`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (aRes.ok) {
          const d = await aRes.json();
          if (!cancelled) setAgents(d?.data?.agents || []);
        }
        if (hRes.ok) {
          const d = await hRes.json();
          if (!cancelled) setHods(d?.data?.hods || []);
        }
      } catch (e) {
        // non-fatal — dropdowns just stay empty
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // ---- reports: scoped server-side by department + date range ----
  const fetchReports = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ department });
      if (from) params.set('date_from', from);
      if (to) params.set('date_to', to);
      const res = await fetch(`${API}/api/ceo/history-reports?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setReports(data.data?.reports || []);
      } else {
        toast.error(data.detail || data.error || 'Could not load history reports');
      }
    } catch (e) {
      toast.error(`Load failed: ${e.message || e}`);
    } finally {
      setLoading(false);
    }
  }, [token, department, from, to]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  // ---- client-side filters: agent / HOD / type / search ----
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return reports.filter((r) => {
      if (agentId !== 'all' && r.author_id !== agentId) return false;
      if (hodId !== 'all' && r.author_id !== hodId) return false;
      if (typeFilter !== 'all' && r.report_type !== typeFilter) return false;
      if (s) {
        const hay = `${r.author_name || ''} ${r.title || ''} ${r.summary || ''} ${r.full_text || ''}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [reports, agentId, hodId, typeFilter, search]);

  // Type tiles read off the agent/HOD/search-filtered set (but NOT the
  // type filter itself) so each tile always shows its true count.
  const tileCounts = useMemo(() => {
    const s = search.trim().toLowerCase();
    const base = reports.filter((r) => {
      if (agentId !== 'all' && r.author_id !== agentId) return false;
      if (hodId !== 'all' && r.author_id !== hodId) return false;
      if (s) {
        const hay = `${r.author_name || ''} ${r.title || ''} ${r.summary || ''} ${r.full_text || ''}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
    return {
      total: base.length,
      agent_daily: base.filter((r) => r.report_type === 'agent_daily').length,
      agent_visit: base.filter((r) => r.report_type === 'agent_visit').length,
      hod_daily: base.filter((r) => r.report_type === 'hod_daily').length,
      hod_visit: base.filter((r) => r.report_type === 'hod_visit').length,
    };
  }, [reports, agentId, hodId, search]);

  // ---- group filtered reports by the selected period ----
  const groups = useMemo(() => {
    const keyOf = (r) => {
      const d = (r.report_date || '').slice(0, 10);
      if (period === 'yearly') return d.slice(0, 4);
      if (period === 'monthly') return d.slice(0, 7);
      return d;
    };
    const map = new Map();
    filtered.forEach((r) => {
      const k = keyOf(r) || 'unknown';
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(r);
    });
    return [...map.entries()]
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))           // newest period first
      .map(([key, items]) => ({ key, items }));
  }, [filtered, period]);

  // "Dirty" = any filter (including the date range and the
  // Daily/Monthly/Yearly period) is active. Drives the Clear button's
  // dot so it reads as active whenever a click would change something.
  const filtersDirty = agentId !== 'all' || hodId !== 'all' || typeFilter !== 'all'
    || !!search || period !== 'daily'
    || !!from || !!to;

  // Reset every filter back to its empty/default state — including
  // emptying the date inputs so the feed shows the full archive again.
  // Department (the Sales/Marketing sub-tab) is intentionally left alone
  // — Clear resets filters, it doesn't switch you to another tab.
  // A toast confirms the click landed even when little changes visually.
  const clearFilters = () => {
    setAgentId('all');
    setHodId('all');
    setTypeFilter('all');
    setSearch('');
    setPeriod('daily');
    setFrom('');
    setTo('');
    setExpandedKey(null);
    toast.success(filtersDirty ? 'Filters cleared' : 'Filters already at defaults');
  };

  // ---- export: CSV (Excel) ----
  const exportCsv = () => {
    if (!filtered.length) { toast('Nothing to export for the current filters'); return; }
    const header = ['Report Date', 'Submitted At', 'Type', 'Department', 'Author', 'Role', 'Title', 'Outcome', 'Summary', 'Full Report'];
    const rows = filtered.map((r) => [
      r.report_date, r.submitted_at, TYPE_META[r.report_type]?.label || r.report_type,
      r.department, r.author_name, r.role, r.title, r.outcome || '',
      r.summary, r.full_text,
    ]);
    const csv = [header, ...rows].map((row) => row.map(csvEscape).join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `history-reports-${department}-${from || 'all'}_to_${to || 'all'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} reports`);
  };

  // ---- export: PDF via a print-styled window ----
  const exportPdf = () => {
    if (!filtered.length) { toast('Nothing to export for the current filters'); return; }
    const win = window.open('', '_blank');
    if (!win) { toast.error('Pop-up blocked — allow pop-ups to print/PDF'); return; }
    const esc = (v) => String(v == null ? '' : v)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const sections = groups.map((g) => `
      <h2>${esc(periodLabel(g.key, period))} <span class="muted">(${g.items.length})</span></h2>
      <table>
        <thead><tr><th>Date</th><th>Type</th><th>Author</th><th>Title</th><th>Summary</th></tr></thead>
        <tbody>
          ${g.items.map((r) => `<tr>
            <td>${esc(r.report_date)}</td>
            <td>${esc(TYPE_META[r.report_type]?.label || r.report_type)}</td>
            <td>${esc(r.author_name)}</td>
            <td>${esc(r.title)}</td>
            <td>${esc(r.summary)}</td>
          </tr>`).join('')}
        </tbody>
      </table>`).join('');
    win.document.write(`<!doctype html><html><head><title>History Reports</title>
      <style>
        body{font-family:Arial,Helvetica,sans-serif;color:#1e293b;padding:24px;}
        h1{font-size:18px;margin:0 0 4px;}
        .sub{color:#64748b;font-size:12px;margin-bottom:16px;}
        h2{font-size:13px;background:#f1f5f9;padding:6px 8px;margin:18px 0 6px;border-radius:4px;}
        .muted{color:#94a3b8;font-weight:normal;}
        table{width:100%;border-collapse:collapse;font-size:11px;}
        th,td{border:1px solid #e2e8f0;padding:5px 7px;text-align:left;vertical-align:top;}
        th{background:#f8fafc;}
        @media print{ h2{break-after:avoid;} tr{break-inside:avoid;} }
      </style></head><body>
      <h1>History Reports — ${department === 'marketing' ? 'Marketing' : 'Sales'}</h1>
      <div class="sub">${from || to ? `${from || 'start'} to ${to || 'today'}` : 'All time'} &middot; ${filtered.length} reports &middot; grouped ${period}</div>
      ${sections}
      <script>window.onload=function(){window.print();}</script>
      </body></html>`);
    win.document.close();
  };

  const toneClasses = (tone, active) => {
    const map = {
      slate:   active ? 'bg-slate-100 border-slate-400 ring-2 ring-slate-300/60'      : 'bg-slate-50 border-slate-200 hover:bg-slate-100',
      violet:  active ? 'bg-violet-100 border-violet-500 ring-2 ring-violet-300/60'   : 'bg-violet-50 border-violet-200 hover:bg-violet-100',
      emerald: active ? 'bg-emerald-100 border-emerald-500 ring-2 ring-emerald-300/60': 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100',
      blue:    active ? 'bg-blue-100 border-blue-500 ring-2 ring-blue-300/60'         : 'bg-blue-50 border-blue-200 hover:bg-blue-100',
      amber:   active ? 'bg-amber-100 border-amber-500 ring-2 ring-amber-300/60'      : 'bg-amber-50 border-amber-200 hover:bg-amber-100',
    };
    return map[tone] || map.slate;
  };

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2 flex-wrap">
              <History size={18} className="text-violet-600" />
              History Reports
              <HelpInfo
                align="inline"
                title="How to use History Reports"
                steps={[
                  'A permanent archive of every Daily Report and Site Visit Report ever filed by agents and HODs — nothing is deleted, so you always have the full trail.',
                  <>Switch the <strong>Sales</strong> / <strong>Marketing</strong> sub-tabs to scope the department, then group the feed <em>Daily</em>, <em>Monthly</em> or <em>Yearly</em>.</>,
                  <>The four tiles (<em>Agent Daily</em>, <em>Agent Site Visit</em>, <em>HOD Daily</em>, <em>HOD Site Visit</em>) are clickable filters — tap one to show only that type.</>,
                  'Narrow further with the date range, Agent and HOD pickers, or the search box (matches author, title and report text).',
                  'Export the current view to CSV (opens in Excel) or PDF (print dialog) for offline review and management reporting.',
                ]}
                tip="Pick one agent + Yearly grouping to see a full year of their activity at a glance — useful for appraisals."
              />
            </CardTitle>
            <CardDescription>
              Complete archive of Agent &amp; HOD daily and site-visit reports — Admin / CEO only
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={exportCsv} disabled={!filtered.length}>
              <Download size={12} className="mr-1" /> Excel / CSV
            </Button>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={exportPdf} disabled={!filtered.length}>
              <Printer size={12} className="mr-1" /> PDF
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={fetchReports} title="Refresh">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Department sub-tabs */}
        <div className="flex gap-1 border-b border-slate-200">
          {[
            { k: 'sales', label: 'Sales Reports' },
            { k: 'marketing', label: 'Marketing Reports' },
          ].map((d) => (
            <button
              key={d.k}
              type="button"
              onClick={() => setDepartment(d.k)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                department === d.k
                  ? 'border-violet-600 text-violet-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {d.label}
            </button>
          ))}
        </div>

        {/* Period toggle + filters */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Daily / Monthly / Yearly */}
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
            {['daily', 'monthly', 'yearly'].map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPeriod(p)}
                className={`px-3 py-1.5 text-xs capitalize transition-colors ${
                  period === p ? 'bg-violet-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                {p}
              </button>
            ))}
          </div>

          <Calendar size={14} className="text-slate-400 ml-1" />
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="h-8 text-xs border border-slate-300 rounded px-2"
          />
          <span className="text-xs text-slate-400">to</span>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="h-8 text-xs border border-slate-300 rounded px-2"
          />

          {/* Agent and HOD are mutually exclusive — you filter by one
              author at a time. Picking an agent clears any HOD
              selection and vice versa, so the two never fight. */}
          <Select
            value={agentId}
            onValueChange={(v) => { setAgentId(v); if (v !== 'all') setHodId('all'); }}
          >
            <SelectTrigger className="h-8 w-[170px] text-xs"><SelectValue placeholder="All agents" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All agents</SelectItem>
              {agents.map((a) => (
                <SelectItem key={a.id || a.agent_id} value={a.id || a.agent_id}>
                  {a.full_name || a.agent_name || a.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={hodId}
            onValueChange={(v) => { setHodId(v); if (v !== 'all') setAgentId('all'); }}
          >
            <SelectTrigger className="h-8 w-[160px] text-xs"><SelectValue placeholder="All HODs" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All HODs</SelectItem>
              {hods.map((h) => (
                <SelectItem key={h.id} value={h.id}>{h.full_name || h.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="relative">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search reports…"
              className="h-8 w-[180px] text-xs pl-7"
            />
          </div>

          {/* Clear — always rendered as a real, active button so it
              never reads as disabled. Resets every filter + the period
              toggle. A dot appears when filters have drifted from
              their defaults, as a hint that a click will change things. */}
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700"
            onClick={clearFilters}
            title="Clear all filters and reset the date range"
          >
            {filtersDirty && <span className="w-1.5 h-1.5 rounded-full bg-rose-500 mr-1.5 inline-block" />}
            Clear
          </Button>
        </div>

        {/* Type tiles — clickable filters */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-center">
          <button
            type="button"
            onClick={() => setTypeFilter('all')}
            className={`rounded-md border py-2 transition-all ${toneClasses('slate', typeFilter === 'all')}`}
          >
            <div className="text-lg font-semibold text-slate-900">{tileCounts.total}</div>
            <div className="text-xs text-slate-500">All Reports</div>
          </button>
          {Object.entries(TYPE_META).map(([key, meta]) => {
            const Icon = meta.icon;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setTypeFilter(typeFilter === key ? 'all' : key)}
                className={`rounded-md border py-2 transition-all ${toneClasses(meta.tone, typeFilter === key)}`}
              >
                <div className="text-lg font-semibold text-slate-900 flex items-center justify-center gap-1">
                  <Icon size={13} /> {tileCounts[key]}
                </div>
                <div className="text-xs text-slate-500">{meta.label}</div>
              </button>
            );
          })}
        </div>

        {/* Grouped report list */}
        {loading ? (
          <p className="text-sm text-slate-500 text-center py-10">Loading history…</p>
        ) : groups.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-10">
            No reports match the current filters.
          </p>
        ) : (
          <div className="space-y-4">
            {groups.map((g) => (
              <div key={g.key}>
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="text-sm font-semibold text-slate-700">{periodLabel(g.key, period)}</h4>
                  <Badge variant="outline" className="text-xs">{g.items.length}</Badge>
                </div>
                <div className="space-y-2">
                  {g.items.map((r) => {
                    const key = `${r.report_type}:${r.id}`;
                    const isOpen = expandedKey === key;
                    const meta = TYPE_META[r.report_type] || {};
                    const Icon = meta.icon || FileText;
                    return (
                      <div key={key} className="rounded-lg border border-slate-200">
                        <button
                          type="button"
                          onClick={() => setExpandedKey(isOpen ? null : key)}
                          className="w-full text-left p-3 flex items-start gap-3 hover:bg-slate-50"
                        >
                          {isOpen ? <ChevronDown size={16} className="mt-0.5 text-slate-400" />
                                  : <ChevronRight size={16} className="mt-0.5 text-slate-400" />}
                          <Icon size={14} className={`mt-0.5 ${
                            meta.tone === 'emerald' ? 'text-emerald-600'
                            : meta.tone === 'blue' ? 'text-blue-600'
                            : meta.tone === 'amber' ? 'text-amber-600'
                            : 'text-violet-600'
                          }`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-slate-900 truncate">{r.title}</span>
                              <span className="text-xs text-slate-500">· {r.author_name}</span>
                              <Badge className="text-xs bg-slate-50 text-slate-600 border border-slate-300">
                                {meta.label || r.report_type}
                              </Badge>
                              {r.report_type.endsWith('visit') && r.productive === true && (
                                <Badge className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-300">
                                  <CheckCircle2 size={10} className="mr-0.5" /> Productive
                                </Badge>
                              )}
                              {r.report_type.endsWith('visit') && r.productive === false && (
                                <Badge className="text-xs bg-amber-50 text-amber-700 border border-amber-300">
                                  <XCircle size={10} className="mr-0.5" /> Not productive
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5 font-mono">
                              {r.report_date} · submitted {fmtDateTime(r.submitted_at)}
                            </p>
                            {!isOpen && (
                              <p className="text-xs text-slate-600 mt-1 line-clamp-2">{r.summary}</p>
                            )}
                          </div>
                        </button>
                        {isOpen && (
                          <div className="px-4 pb-4 border-t border-slate-100 bg-slate-50/60 text-sm pt-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                              Full report
                            </p>
                            <pre className="text-slate-800 whitespace-pre-wrap font-sans text-sm">
                              {r.full_text || '(no content)'}
                            </pre>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default HistoryReportsTab;
